/**
 * The printable body of a NICU Admission Form.
 *
 * Pass any admission-shaped object: a saved record from the API/list, or the
 * live form data mapped to the same field names. Keeping this in one file means
 * the admission page, the view modal and the patient list all print the same
 * sheet — change it here and every copy follows.
 *
 * Usage:
 *   <NicuPrintLayout title="NICU Admission Form"
 *     billInfo={<NicuAdmissionPrintInfo admission={record} />} />
 */
const NicuAdmissionPrintInfo = ({ admission = {} }) => {
  const value = (v) => (v === 0 || v ? v : "--");

  const formatDate = (d) => {
    if (!d) return "--";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "--" : date.toLocaleDateString("en-GB");
  };

  // Village / road first, then upazila, district, division — empty parts drop out
  const fullAddress =
    [admission.address, admission.upazila, admission.district, admission.division]
      .filter(Boolean)
      .join(", ") || "N/A";

  return (
    <div className="w-full font-sans text-gray-800">
      {/* --- TOP: NAME + REG NO --- */}
      <div className="flex justify-between items-end border-b-2 border-gray-800 pb-3 mb-5">
        <div className="pr-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
            Patient Name
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 uppercase leading-none whitespace-nowrap">
            {admission.patientName || "Unknown Patient"}
          </h2>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
            Registration No
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 leading-none whitespace-nowrap">
            {admission.regNo || "N/A"}
          </h2>
        </div>
      </div>

      {/* --- SECTION 1: PATIENT & GUARDIAN --- */}
      <div className="mb-6">
        <h3 className="text-sm font-bold uppercase tracking-wider border-b border-gray-400 pb-1 mb-3">
          Patient &amp; Guardian Information
        </h3>
        <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-base">
          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Age / Sex: </span>
            <span className="font-bold whitespace-nowrap">
              {value(admission.age)} / {value(admission.sex)}
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Father: </span>
            <span className="font-bold whitespace-nowrap">{value(admission.fatherName)}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Date of Birth: </span>
            <span className="font-bold whitespace-nowrap">{formatDate(admission.dateOfBirth)}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Mother: </span>
            <span className="font-bold whitespace-nowrap">{value(admission.motherName)}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Religion: </span>
            <span className="font-bold whitespace-nowrap">{value(admission.religion)}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Guardian: </span>
            <span className="font-bold whitespace-nowrap">{value(admission.relativeName)}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Mobile: </span>
            <span className="font-bold whitespace-nowrap">{value(admission.phoneNumber)}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold whitespace-nowrap">
              Contact Person:{" "}
            </span>
            <span className="font-bold whitespace-nowrap">{value(admission.agentName)}</span>
          </div>

          {/* full width — never breaks */}
          <div className="col-span-2 flex items-baseline pt-1">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Full Address: </span>
            <span className="font-bold">
              {fullAddress}
              {admission.careOf ? ` (C/O: ${admission.careOf})` : ""}
            </span>
          </div>
        </div>
      </div>

      {/* --- SECTION 2: ADMISSION DETAILS --- */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider border-b border-gray-400 pb-1 mb-3">
          Admission Details
        </h3>
        <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-base">
          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Admit Date: </span>
            <span className="font-bold whitespace-nowrap">{formatDate(admission.admissionDate)}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Status: </span>
            <span className="font-bold whitespace-nowrap">{admission.status || "Admitted"}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Admit Time: </span>
            <span className="font-bold whitespace-nowrap">{value(admission.admissionTime)}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Bill Type: </span>
            <span className="font-bold whitespace-nowrap">{admission.billingType || "Normal"}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Bed No: </span>
            <span className="font-bold whitespace-nowrap border border-gray-400 px-2">
              {admission.bedNo || "None"}
            </span>
          </div>
          <div></div>

          {/* full width — long name never breaks */}
          <div className="col-span-2 flex items-baseline pt-1">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Consultant: </span>
            <span className="font-bold whitespace-nowrap">{admission.consultantName || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* --- METADATA --- */}
      <div className="mt-6 pt-2 border-t border-dashed border-gray-300 flex justify-between text-sm text-gray-500">
        <span>
          Entry By: {admission.employeeName || "--"} ({admission.employeeCode || "--"})
        </span>
        <span>Printed: {new Date().toLocaleString("en-GB")}</span>
      </div>
    </div>
  );
};

export default NicuAdmissionPrintInfo;
